import {ref, computed} from 'vue';
import {defineStore} from 'pinia';
import {useLogger} from '#imports';
import {readonlyState} from '@malt/utils';
import {AccountResourceApi, Configuration, PhoneValidationStatus} from '@api';

const configuration = new Configuration({basePath: ''});
const accountApi = new AccountResourceApi(configuration);

export const usePhoneNumberStore = defineStore('phone-number-store', () => {
    const logger = useLogger();
    const phone = ref<string | undefined>('');
    const savedPhoneNumber = ref<string>();
    const phoneNumberStatus = ref<PhoneValidationStatus>();
    const phoneNumberVerificationCodeModel = ref<string>('');

    async function getPhoneNumber(): Promise<void> {
        try {
            const response = await accountApi.getPhone();
            if (!phone.value) {
                phone.value = response.number;
            }
            savedPhoneNumber.value = response.number;
        } catch (e) {
            logger.error('Error while fetching phone number', e);
        }
    }

    async function getPhoneNumberValidationStatus() {
        try {
            phoneNumberStatus.value = await accountApi.getPhoneValidationStatus();
        } catch (e) {
            logger.error('Error while fetching phone validation status', e);
        }
    }

    const hasPhoneNumberChanged = computed(() => {
        const inputPhoneDigits = phone.value?.replace(/\s+/g, '') || '';
        const dbPhoneDigits = savedPhoneNumber.value?.replace(/\s+/g, '') || '';
        return inputPhoneDigits !== dbPhoneDigits;
    });
    const isPhoneStatusValidated = computed(() => phoneNumberStatus.value === PhoneValidationStatus.PhoneNumberValidated);
    const isPhoneStatusNeedValidation = computed(() => phoneNumberStatus.value === PhoneValidationStatus.PhoneNumberNeedValidation);
    const isPhoneNumberMissing = computed(() => phoneNumberStatus.value === PhoneValidationStatus.PhoneNumberMissing);

    return {
        phone: readonlyState(phone),
        savedPhoneNumber: readonlyState(savedPhoneNumber),
        phoneNumberStatus: readonlyState(phoneNumberStatus),
        phoneNumberVerificationCodeModel: readonlyState(phoneNumberVerificationCodeModel),
        hasPhoneNumberChanged,
        isPhoneStatusNeedValidation,
        isPhoneStatusValidated,
        isPhoneNumberMissing,
        getPhoneNumber,
        getPhoneNumberValidationStatus,
    };
});
